import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Lock, ChevronRight, Mail, MapPin, Globe } from 'lucide-react';
import Footer from '../components/karma/Footer';

const sections = [
  { id: 'sec-1', num: '1', title: 'Who We Are' },
  { id: 'sec-2', num: '2', title: 'Our Commitment to Privacy' },
  { id: 'sec-3', num: '3', title: 'Personal Data We Collect' },
  { id: 'sec-4', num: '4', title: 'Where We Collect It' },
  { id: 'sec-5', num: '5', title: 'Automated Technologies' },
  { id: 'sec-6', num: '6', title: 'Third Parties & Public Sources' },
  { id: 'sec-7', num: '7', title: 'Automated Decision-Making' },
  { id: 'sec-8', num: '8', title: 'How We Use Your Data' },
  { id: 'sec-9', num: '9', title: 'Cookies & Analytics' },
  { id: 'sec-10', num: '10', title: 'Email & Marketing' },
  { id: 'sec-11', num: '11', title: 'Sharing Your Personal Data' },
  { id: 'sec-12', num: '12', title: 'Third-Party Disclosures' },
  { id: 'sec-13', num: '13', title: 'Anonymised Data' },
  { id: 'sec-14', num: '14', title: 'International Transfers' },
  { id: 'sec-15', num: '15', title: 'Data Security' },
  { id: 'sec-16', num: '16', title: 'Data Retention' },
  { id: 'sec-17', num: '17', title: 'Your Legal Rights' },
  { id: 'sec-18', num: '18', title: 'Changes to This Policy' },
  { id: 'sec-19', num: '19', title: 'Contacting Us & DPO' },
];

const PrivacyPolicy = () => {
  const [activeSection, setActiveSection] = useState('sec-1');

  useEffect(() => {
    document.title = 'Privacy Policy — Karma Experience';
    window.scrollTo(0, 0);

    const handleScroll = () => {
      const scrollPos = window.scrollY + 180;
      for (let i = sections.length - 1; i >= 0; i--) {
        const el = document.getElementById(sections[i].id);
        if (el && el.offsetTop <= scrollPos) {
          setActiveSection(sections[i].id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.pageYOffset - 110;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <div className="bg-[#FBF7EE] min-h-screen text-[#4a5568]">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#0F2340] border-b border-[#B8934A]/20 shadow-md">
        <div className="max-w-[1240px] mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="/images/logo/KE-white.png"
              alt="Karma Experience"
              className="h-9 w-auto object-contain"
            />
          </Link>

          <div className="flex items-center gap-4">
            <Link
              to="/activate"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-[#B8934A] hover:text-[#C9A75E] border border-[#B8934A]/40 hover:border-[#B8934A] rounded-full px-4 py-2 transition-all"
            >
              <ArrowLeft size={14} />
              <span>Back to Activation</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Banner */}
      <section className="bg-gradient-to-b from-[#0F2340] to-[#142A4A] text-white py-14 border-b border-[#B8934A]/20">
        <div className="max-w-[1240px] mx-auto px-6">
          <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-[#B8934A]/15 border border-[#B8934A]/30 text-[#B8934A] text-xs uppercase tracking-[0.14em] font-semibold mb-4">
            <Lock size={13} />
            <span>GDPR &amp; Privacy Compliance</span>
          </div>
          <h1 className="font-serif-editorial text-4xl md:text-5xl lg:text-6xl font-normal text-[#FBF7EE] tracking-tight mb-4">
            Privacy Policy
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-xs md:text-sm text-[#C9A75E]">
            <span>Karma Resorts Pte Ltd</span>
            <span className="opacity-50">•</span>
            <span>UK GDPR &amp; EU GDPR Compliant</span>
            <span className="opacity-50">•</span>
            <span>Last Updated: 1 January 2026</span>
          </div>
        </div>
      </section>

      {/* Main Content Layout */}
      <div className="max-w-[1240px] mx-auto px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          {/* Sticky Sidebar Navigation */}
          <aside className="hidden lg:block lg:col-span-4 sticky top-28 bg-white border border-[#E5DFD3] rounded-2xl p-6 shadow-sm max-h-[calc(100vh-140px)] overflow-y-auto">
            <div className="font-serif-editorial text-xl font-semibold text-[#0F2340] pb-3 mb-4 border-b border-[#EEE9DC]">
              Privacy Table of Contents
            </div>
            <nav className="space-y-1">
              {sections.map((sec) => (
                <button
                  key={sec.id}
                  onClick={() => scrollTo(sec.id)}
                  className={`w-full text-left flex items-center justify-between px-3 py-1.5 rounded-lg text-xs transition-all ${
                    activeSection === sec.id
                      ? 'bg-[#FBF3E1] text-[#967739] font-medium pl-4'
                      : 'text-[#55606E] hover:bg-[#FBF7EE] hover:text-[#0F2340]'
                  }`}
                >
                  <span className="truncate">
                    <span className="text-[#B8934A] font-semibold mr-1.5">{sec.num}.</span>
                    {sec.title}
                  </span>
                  <ChevronRight size={13} className={`shrink-0 ${activeSection === sec.id ? 'opacity-100' : 'opacity-30'}`} />
                </button>
              ))}
            </nav>

            <div className="mt-6 pt-5 border-t border-[#EEE9DC] text-xs text-[#718096]">
              Data Protection Officer:<br />
              <a href="mailto:dpo@karmagroup.com" className="text-[#B8934A] underline font-medium">
                dpo@karmagroup.com
              </a>
            </div>
          </aside>

          {/* Legal Clauses Card */}
          <main className="lg:col-span-8 bg-white border border-[#E5DFD3] rounded-2xl p-6 md:p-10 shadow-sm leading-relaxed text-[15px]">
            <div className="text-[#0F2340] font-medium text-base md:text-lg pb-6 mb-8 border-b border-[#EEE9DC]">
              At Karma Experience, we are committed to respecting and protecting your privacy. This policy outlines how we collect, store, process, and safeguard your personal information in compliance with the UK GDPR, EU GDPR, and Singapore Personal Data Protection Act (PDPA).
            </div>

            {/* Section 1 */}
            <section id="sec-1" className="mb-10 scroll-mt-28">
              <div className="flex items-baseline gap-3 mb-3">
                <span className="font-serif-editorial text-2xl font-bold text-[#B8934A]">1.</span>
                <h2 className="font-serif-editorial text-2xl md:text-3xl font-semibold text-[#0F2340]">Who We Are</h2>
              </div>
              <p className="mb-4">
                Karma Experience is a trading name of <strong>Karma Resorts Pte Ltd</strong>, incorporated in Singapore (UEN: 200509501G) with registered address at 160 Robinson Road #18-07, Singapore 068914.
              </p>
              <p>
                Karma Resorts Pte Ltd acts as the primary Data Controller responsible for your personal information. References in this policy to &ldquo;Karma&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo; or &ldquo;our&rdquo; refer to Karma Resorts Pte Ltd and its relevant resort affiliates.
              </p>
            </section>

            {/* Section 2 */}
            <section id="sec-2" className="mb-10 scroll-mt-28">
              <div className="flex items-baseline gap-3 mb-3">
                <span className="font-serif-editorial text-2xl font-bold text-[#B8934A]">2.</span>
                <h2 className="font-serif-editorial text-2xl md:text-3xl font-semibold text-[#0F2340]">Our Commitment to Privacy</h2>
              </div>
              <p className="mb-4">
                We are committed to processing your personal data lawfully, fairly, and transparently. We collect data solely for specified, explicit, and legitimate business purposes and do not process it in a manner incompatible with those purposes.
              </p>
              <p>
                We maintain appropriate organizational and technical safeguards to ensure adequate security against unauthorized access, loss, destruction, or disclosure.
              </p>
            </section>

            {/* Section 3 */}
            <section id="sec-3" className="mb-10 scroll-mt-28">
              <div className="flex items-baseline gap-3 mb-3">
                <span className="font-serif-editorial text-2xl font-bold text-[#B8934A]">3.</span>
                <h2 className="font-serif-editorial text-2xl md:text-3xl font-semibold text-[#0F2340]">Personal Data We Collect</h2>
              </div>
              <p className="mb-3">Depending on how you interact with us, we may collect and process:</p>
              <ul className="space-y-2 list-none pl-1 mb-4">
                <li className="flex items-start gap-2">
                  <span className="text-[#B8934A] font-bold">•</span>
                  <span><strong>Identity Data:</strong> First name, last name, title, date of birth, marital status, and passport/national ID details for resort check-in;</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#B8934A] font-bold">•</span>
                  <span><strong>Contact Data:</strong> Residential address, email address, country code, and telephone/mobile numbers;</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#B8934A] font-bold">•</span>
                  <span><strong>Transaction Data:</strong> Voucher activation references, payment confirmation numbers, package selection, and booking dates;</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#B8934A] font-bold">•</span>
                  <span><strong>Technical Data:</strong> IP address, browser type, device information, operating system, and website usage statistics.</span>
                </li>
              </ul>
              <p className="text-xs text-[#718096]">
                Note: We do not store credit card CVV numbers or full payment card credentials on our servers. All payments are encrypted and handled directly by PCI-DSS compliant providers (e.g. Flywire).
              </p>
            </section>

            {/* Section 4 */}
            <section id="sec-4" className="mb-10 scroll-mt-28">
              <div className="flex items-baseline gap-3 mb-3">
                <span className="font-serif-editorial text-2xl font-bold text-[#B8934A]">4.</span>
                <h2 className="font-serif-editorial text-2xl md:text-3xl font-semibold text-[#0F2340]">Where We Collect Personal Data</h2>
              </div>
              <p className="mb-3">We collect data directly from you through:</p>
              <ul className="space-y-2 list-none pl-1">
                <li className="flex items-start gap-2"><span className="text-[#B8934A] font-bold">•</span><span>Online voucher activation forms and enquiry submissions;</span></li>
                <li className="flex items-start gap-2"><span className="text-[#B8934A] font-bold">•</span><span>Phone conversations with our travel concierge or reservation specialists;</span></li>
                <li className="flex items-start gap-2"><span className="text-[#B8934A] font-bold">•</span><span>Event registrations, brand partnerships, or survey participations;</span></li>
                <li className="flex items-start gap-2"><span className="text-[#B8934A] font-bold">•</span><span>Guest registration cards filled out upon resort arrival.</span></li>
              </ul>
            </section>

            {/* Section 5 */}
            <section id="sec-5" className="mb-10 scroll-mt-28">
              <div className="flex items-baseline gap-3 mb-3">
                <span className="font-serif-editorial text-2xl font-bold text-[#B8934A]">5.</span>
                <h2 className="font-serif-editorial text-2xl md:text-3xl font-semibold text-[#0F2340]">Automated Technologies</h2>
              </div>
              <p>
                As you interact with our website, we automatically collect technical information about your equipment, browsing actions, and patterns using cookies, server logs, and tracking tags. Please review Section 9 for complete details.
              </p>
            </section>

            {/* Section 6 */}
            <section id="sec-6" className="mb-10 scroll-mt-28">
              <div className="flex items-baseline gap-3 mb-3">
                <span className="font-serif-editorial text-2xl font-bold text-[#B8934A]">6.</span>
                <h2 className="font-serif-editorial text-2xl md:text-3xl font-semibold text-[#0F2340]">Third Parties &amp; Publicly Available Sources</h2>
              </div>
              <p>
                We may receive personal data about you from verified third parties, such as certified promotional marketing partners, co-branded campaign sponsors, and analytics providers (such as Google Analytics).
              </p>
            </section>

            {/* Section 7 */}
            <section id="sec-7" className="mb-10 scroll-mt-28">
              <div className="flex items-baseline gap-3 mb-3">
                <span className="font-serif-editorial text-2xl font-bold text-[#B8934A]">7.</span>
                <h2 className="font-serif-editorial text-2xl md:text-3xl font-semibold text-[#0F2340]">Automated Decision-Making</h2>
              </div>
              <p>
                We do not make automated decisions or perform profiling that produces legal or similarly significant effects regarding our guests.
              </p>
            </section>

            {/* Section 8 */}
            <section id="sec-8" className="mb-10 scroll-mt-28">
              <div className="flex items-baseline gap-3 mb-3">
                <span className="font-serif-editorial text-2xl font-bold text-[#B8934A]">8.</span>
                <h2 className="font-serif-editorial text-2xl md:text-3xl font-semibold text-[#0F2340]">How We Use Your Personal Data</h2>
              </div>
              <p className="mb-3">We only use your personal data where the law allows us to. Most commonly:</p>
              <ul className="space-y-2 list-none pl-1 mb-4">
                <li className="flex items-start gap-2"><span className="text-[#B8934A] font-bold">•</span><span>To process and fulfil your voucher activation and holiday booking;</span></li>
                <li className="flex items-start gap-2"><span className="text-[#B8934A] font-bold">•</span><span>To communicate booking confirmations, itinerary updates, and pre-arrival itineraries;</span></li>
                <li className="flex items-start gap-2"><span className="text-[#B8934A] font-bold">•</span><span>To manage payments, fees, refunds, and collections;</span></li>
                <li className="flex items-start gap-2"><span className="text-[#B8934A] font-bold">•</span><span>To satisfy legal obligations and hotel guest registry statutory compliance.</span></li>
              </ul>
            </section>

            {/* Section 9 */}
            <section id="sec-9" className="mb-10 scroll-mt-28">
              <div className="flex items-baseline gap-3 mb-3">
                <span className="font-serif-editorial text-2xl font-bold text-[#B8934A]">9.</span>
                <h2 className="font-serif-editorial text-2xl md:text-3xl font-semibold text-[#0F2340]">Cookies &amp; Analytics</h2>
              </div>
              <p className="mb-4">
                Our website uses cookies to distinguish you from other users. This helps us provide you with an optimal browsing experience, remember your preferences, and improve website speed and performance.
              </p>
              <p>
                You can configure your browser to block all or some cookies. However, if you disable strictly necessary cookies, some features of the voucher activation portal may not operate properly.
              </p>
            </section>

            {/* Section 10 */}
            <section id="sec-10" className="mb-10 scroll-mt-28">
              <div className="flex items-baseline gap-3 mb-3">
                <span className="font-serif-editorial text-2xl font-bold text-[#B8934A]">10.</span>
                <h2 className="font-serif-editorial text-2xl md:text-3xl font-semibold text-[#0F2340]">Email &amp; Marketing Communications</h2>
              </div>
              <p className="mb-4">
                You may receive marketing communications from Karma Experience if you opted in or requested information about our exclusive destinations and private membership offers.
              </p>
              <p>
                You can unsubscribe from marketing emails at any time by clicking the &ldquo;Unsubscribe&rdquo; link in any email or emailing our concierge desk.
              </p>
            </section>

            {/* Section 11 */}
            <section id="sec-11" className="mb-10 scroll-mt-28">
              <div className="flex items-baseline gap-3 mb-3">
                <span className="font-serif-editorial text-2xl font-bold text-[#B8934A]">11.</span>
                <h2 className="font-serif-editorial text-2xl md:text-3xl font-semibold text-[#0F2340]">Sharing Your Personal Data</h2>
              </div>
              <p>
                We may share your personal data within the Karma Group of companies (including local operating resort entities) to manage and service your reservations. We never sell your personal data to third parties.
              </p>
            </section>

            {/* Section 12 */}
            <section id="sec-12" className="mb-10 scroll-mt-28">
              <div className="flex items-baseline gap-3 mb-3">
                <span className="font-serif-editorial text-2xl font-bold text-[#B8934A]">12.</span>
                <h2 className="font-serif-editorial text-2xl md:text-3xl font-semibold text-[#0F2340]">Third-Party Service Disclosures</h2>
              </div>
              <p>
                We share data with trusted third-party service providers acting as data processors who assist in operating our IT systems, secure cloud hosting, payment gateways (Flywire), and customer relationship platforms (LeadSquared), all under strict confidentiality agreements.
              </p>
            </section>

            {/* Section 13 */}
            <section id="sec-13" className="mb-10 scroll-mt-28">
              <div className="flex items-baseline gap-3 mb-3">
                <span className="font-serif-editorial text-2xl font-bold text-[#B8934A]">13.</span>
                <h2 className="font-serif-editorial text-2xl md:text-3xl font-semibold text-[#0F2340]">Anonymised Data</h2>
              </div>
              <p>
                We may aggregate statistical or demographic data (&ldquo;Aggregated Data&rdquo;) derived from your personal data. Aggregated Data does not directly or indirectly reveal your identity and is not considered personal data under data protection laws.
              </p>
            </section>

            {/* Section 14 */}
            <section id="sec-14" className="mb-10 scroll-mt-28">
              <div className="flex items-baseline gap-3 mb-3">
                <span className="font-serif-editorial text-2xl font-bold text-[#B8934A]">14.</span>
                <h2 className="font-serif-editorial text-2xl md:text-3xl font-semibold text-[#0F2340]">International Transfers</h2>
              </div>
              <p className="mb-4">
                Because Karma operates luxury resorts globally (across the UK, Europe, and Asia-Pacific), your personal data may be transferred to and stored in destinations outside the UK and European Economic Area (EEA).
              </p>
              <p>
                When transferring personal data internationally, we ensure appropriate safeguards are implemented, such as UK Standard Contractual Clauses (SCCs) or International Data Transfer Agreements (IDTAs).
              </p>
            </section>

            {/* Section 15 */}
            <section id="sec-15" className="mb-10 scroll-mt-28">
              <div className="flex items-baseline gap-3 mb-3">
                <span className="font-serif-editorial text-2xl font-bold text-[#B8934A]">15.</span>
                <h2 className="font-serif-editorial text-2xl md:text-3xl font-semibold text-[#0F2340]">Data Security</h2>
              </div>
              <p>
                We employ comprehensive security measures including SSL/TLS 256-bit encryption, strict access controls, and network firewalls to protect your personal data from accidental loss, disclosure, or unauthorized alteration.
              </p>
            </section>

            {/* Section 16 */}
            <section id="sec-16" className="mb-10 scroll-mt-28">
              <div className="flex items-baseline gap-3 mb-3">
                <span className="font-serif-editorial text-2xl font-bold text-[#B8934A]">16.</span>
                <h2 className="font-serif-editorial text-2xl md:text-3xl font-semibold text-[#0F2340]">Data Retention</h2>
              </div>
              <p>
                We retain personal data only for as long as necessary to fulfill the purposes for which it was collected, including satisfying legal, accounting, tax, or reporting requirements.
              </p>
            </section>

            {/* Section 17 */}
            <section id="sec-17" className="mb-10 scroll-mt-28">
              <div className="flex items-baseline gap-3 mb-3">
                <span className="font-serif-editorial text-2xl font-bold text-[#B8934A]">17.</span>
                <h2 className="font-serif-editorial text-2xl md:text-3xl font-semibold text-[#0F2340]">Your Legal Rights</h2>
              </div>
              <p className="mb-3">Under UK and EU data protection regulations, you have rights including:</p>
              <ul className="space-y-2 list-none pl-1 mb-4">
                <li className="flex items-start gap-2"><span className="text-[#B8934A] font-bold">•</span><span><strong>Right of Access:</strong> Request a copy of the personal data we hold about you;</span></li>
                <li className="flex items-start gap-2"><span className="text-[#B8934A] font-bold">•</span><span><strong>Right to Rectification:</strong> Request correction of inaccurate or incomplete data;</span></li>
                <li className="flex items-start gap-2"><span className="text-[#B8934A] font-bold">•</span><span><strong>Right to Erasure:</strong> Request deletion of your data where no lawful basis persists;</span></li>
                <li className="flex items-start gap-2"><span className="text-[#B8934A] font-bold">•</span><span><strong>Right to Object:</strong> Object to processing of your data for direct marketing purposes.</span></li>
              </ul>
            </section>

            {/* Section 18 */}
            <section id="sec-18" className="mb-10 scroll-mt-28">
              <div className="flex items-baseline gap-3 mb-3">
                <span className="font-serif-editorial text-2xl font-bold text-[#B8934A]">18.</span>
                <h2 className="font-serif-editorial text-2xl md:text-3xl font-semibold text-[#0F2340]">Changes to This Policy</h2>
              </div>
              <p>
                We review this Privacy Policy periodically. Any updates will be published immediately on this page with a revised &ldquo;Last Updated&rdquo; timestamp.
              </p>
            </section>

            {/* Section 19 */}
            <section id="sec-19" className="scroll-mt-28">
              <div className="flex items-baseline gap-3 mb-3">
                <span className="font-serif-editorial text-2xl font-bold text-[#B8934A]">19.</span>
                <h2 className="font-serif-editorial text-2xl md:text-3xl font-semibold text-[#0F2340]">Contacting Us &amp; Data Protection Officer</h2>
              </div>
              <p className="mb-4">
                If you have questions about this policy or wish to exercise any of your data protection rights, please contact our Data Protection Officer:
              </p>
              <div className="bg-[#FBF7EE] border border-[#E5DFD3] p-5 rounded-xl text-sm space-y-3">
                <div className="flex items-center gap-2 text-[#0F2340] font-semibold">
                  <Mail size={16} className="text-[#B8934A]" />
                  <span>DPO Email:</span>
                  <a href="mailto:dpo@karmagroup.com" className="text-[#B8934A] underline font-normal">
                    dpo@karmagroup.com
                  </a>
                </div>
                <div className="flex items-center gap-2 text-[#0F2340] font-semibold">
                  <Mail size={16} className="text-[#B8934A]" />
                  <span>Guest Services:</span>
                  <a href="mailto:info@karmagroup.com" className="text-[#B8934A] underline font-normal">
                    info@karmagroup.com
                  </a>
                </div>
                <div className="flex items-start gap-2 text-[#0F2340] font-semibold">
                  <MapPin size={16} className="text-[#B8934A] shrink-0 mt-0.5" />
                  <div>
                    <span>Mailing Address:</span>
                    <div className="font-normal text-[#55606E]">
                      Karma Resorts Pte Ltd, 160 Robinson Road #18-07, Singapore 068914
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
